import { useEffect, useState, useCallback } from 'react'
import {
  Plus, Search, Pencil, Trash2, X, ArrowUp, ArrowDown, Download, ExternalLink,
  Video, FileText, Presentation, Image as ImageIcon, FileArchive, Link2, GraduationCap, Megaphone, Tag, BookOpen, Star,
} from 'lucide-react'
import api from '../api/client'
import Pagination from '../components/Pagination'
import { successModal, errorModal, confirmModal } from '../components/ui'

function load(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }

const TIPO_ICON = {
  video: Video, pdf: FileText, apresentacao: Presentation, imagem: ImageIcon, zip: FileArchive,
  link: Link2, treinamento: GraduationCap, campanha: Megaphone, promocional: Tag, curso: BookOpen,
}
const TIPO_LABEL = {
  video: 'Vídeo', pdf: 'PDF', apresentacao: 'Apresentação', imagem: 'Imagem', zip: 'Arquivo ZIP',
  link: 'Link externo', treinamento: 'Treinamento', campanha: 'Campanha', promocional: 'Promocional', curso: 'Curso',
}
const baixavel = (t) => ['pdf', 'apresentacao', 'imagem', 'zip'].includes(t)

export default function Material() {
  const isAdmin = (load('user')?.perfil || '') === 'admin'
  const [itens, setItens] = useState([])
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [meta, setMeta] = useState({ total: 0, pages: 1 })
  const [cats, setCats] = useState([])
  const [tipos, setTipos] = useState([])
  const [filtro, setFiltro] = useState({ q: '', categoria: '', tipo: '' })
  const [editing, setEditing] = useState(null)

  useEffect(() => { api.get('/materiais/categorias').then((r) => { setCats(r.data.categorias || []); setTipos(r.data.tipos || []) }).catch(() => {}) }, [])

  const carregar = useCallback((p = 1, sz = size) => {
    const qs = new URLSearchParams({ page: p, limit: sz })
    Object.entries(filtro).forEach(([k, v]) => v && qs.append(k, v))
    api.get('/materiais?' + qs.toString()).then((r) => {
      setItens(r.data.data || []); setMeta({ total: r.data.total || 0, pages: r.data.pages || 1 }); setPage(p)
    }).catch(() => {})
  }, [filtro, size])
  useEffect(() => { carregar(1) }, [carregar])

  const abrir = (m) => {
    api.post(`/materiais/${m.id}/visualizar`).catch(() => {})
    if (m.url) {
      if (baixavel(m.tipo)) api.post(`/materiais/${m.id}/download`).catch(() => {})
      window.open(m.url, '_blank')
    }
  }
  const excluir = (m) => confirmModal(`Excluir o material "${m.titulo}"?`).then((ok) => {
    if (!ok) return
    api.delete(`/materiais/${m.id}`).then(() => { successModal('Material excluído.'); carregar(page) }).catch(() => errorModal('Erro ao excluir.'))
  })
  const mover = (m, dir) => {
    const idx = itens.findIndex((x) => x.id === m.id)
    const alvo = itens[idx + dir]; if (!alvo) return
    api.put('/materiais/reordenar', { itens: [{ id: m.id, ordem: alvo.ordem }, { id: alvo.id, ordem: m.ordem }] })
      .then(() => carregar(page)).catch(() => {})
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-heading font-bold text-gray-900">Material de Apoio</h1>
        {isAdmin && (
          <button onClick={() => setEditing({ tipo: 'pdf', titulo: '', categoria: '', ordem: 0, ativo: true, obrigatorio: false })} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-800">
            <Plus size={16} /> Novo material
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={filtro.q} onChange={(e) => setFiltro({ ...filtro, q: e.target.value })} placeholder="Buscar material..." className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg" />
        </div>
        <select value={filtro.categoria} onChange={(e) => setFiltro({ ...filtro, categoria: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
          <option value="">Todas categorias</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filtro.tipo} onChange={(e) => setFiltro({ ...filtro, tipo: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
          <option value="">Todos tipos</option>
          {tipos.map((t) => <option key={t} value={t}>{TIPO_LABEL[t] || t}</option>)}
        </select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {itens.map((m, i) => {
          const Ic = TIPO_ICON[m.tipo] || FileText
          return (
            <div key={m.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col">
              <div className="flex items-start justify-between">
                <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-accent-600"><Ic size={14} /> {TIPO_LABEL[m.tipo] || m.tipo}</span>
                {m.obrigatorio && <span className="text-[10px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">OBRIGATÓRIO</span>}
              </div>
              <p className="font-medium mt-1 text-gray-800">{m.titulo}</p>
              {m.categoria && <p className="text-[11px] text-gray-400 mt-0.5">{m.categoria}</p>}
              <p className="text-sm text-gray-500 mt-1 flex-1">{m.descricao}</p>
              <div className="flex items-center justify-between mt-3">
                <button onClick={() => abrir(m)} className="text-sm font-semibold text-accent-600 hover:text-accent-700 flex items-center gap-1">
                  {baixavel(m.tipo) ? <><Download size={14} /> Baixar</> : <><ExternalLink size={14} /> Abrir</>}
                </button>
                {isAdmin && (
                  <div className="flex items-center gap-1 text-gray-400">
                    <button onClick={() => mover(m, -1)} disabled={i === 0} className="p-1 hover:text-gray-700 disabled:opacity-30"><ArrowUp size={14} /></button>
                    <button onClick={() => mover(m, 1)} disabled={i === itens.length - 1} className="p-1 hover:text-gray-700 disabled:opacity-30"><ArrowDown size={14} /></button>
                    <button onClick={() => setEditing(m)} className="p-1 hover:text-blue-600"><Pencil size={14} /></button>
                    <button onClick={() => excluir(m)} className="p-1 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
              {isAdmin && <p className="text-[10px] text-gray-300 mt-1">{m.visualizacoes || 0} views · {m.downloads || 0} downloads {m.ativo ? '' : '· inativo'}</p>}
            </div>
          )
        })}
        {itens.length === 0 && <p className="text-gray-500">Nenhum material publicado ainda.</p>}
      </div>
      <Pagination page={page} pages={meta.pages} total={meta.total} pageSize={size} onPage={carregar} onPageSize={(n) => { setSize(n); carregar(1, n) }} />

      {editing && <MaterialForm material={editing} cats={cats} tipos={tipos} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); carregar(page) }} />}
    </div>
  )
}

function MaterialForm({ material, cats, tipos, onClose, onSaved }) {
  const novo = !material.id
  const [f, setF] = useState({
    tipo: material.tipo || 'pdf', titulo: material.titulo || '', descricao: material.descricao || '',
    url: material.url || '', categoria: material.categoria || '', ordem: material.ordem || 0,
    ativo: material.ativo !== false, obrigatorio: !!material.obrigatorio,
  })
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })
  const salvar = () => {
    if (!f.titulo.trim()) return errorModal('Informe o título.')
    setSaving(true)
    const req = novo ? api.post('/materiais', f) : api.put(`/materiais/${material.id}`, f)
    req.then(() => { successModal(novo ? 'Material publicado!' : 'Material atualizado.'); onSaved() })
      .catch(() => errorModal('Erro ao salvar material.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading font-bold">{novo ? 'Novo material' : 'Editar material'}</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <input value={f.titulo} onChange={set('titulo')} placeholder="Título *" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <textarea value={f.descricao} onChange={set('descricao')} rows={2} placeholder="Descrição" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <input value={f.url} onChange={set('url')} placeholder="URL do conteúdo (link, PDF, vídeo...)" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.tipo} onChange={set('tipo')} className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
            {tipos.map((t) => <option key={t} value={t}>{TIPO_LABEL[t] || t}</option>)}
          </select>
          <select value={f.categoria} onChange={set('categoria')} className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
            <option value="">Categoria...</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-1.5"><input type="number" value={f.ordem} onChange={set('ordem')} className="w-16 border border-gray-200 rounded-lg px-2 py-1" /> Ordem</label>
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={f.ativo} onChange={set('ativo')} /> Ativo</label>
          <label className="flex items-center gap-1.5 cursor-pointer text-red-600"><input type="checkbox" checked={f.obrigatorio} onChange={set('obrigatorio')} /> <Star size={13} /> Treinamento obrigatório</label>
        </div>
        <button onClick={salvar} disabled={saving} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar'}</button>
      </div>
    </div>
  )
}
